import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AdminLearningContentRow,
  AdminLearningContentRowUpsert,
  AdminLearningContentSubCategory,
} from '../types/domain';
import {
  getAdminLearningContentRows,
  replaceAdminLearningContentRows,
} from '../services/adminLearningContentService';
import { loadAdminLearningContentRowsCacheAsync } from '../utils/adminLearningContentCache';
import { isAdminLearningContentRowsSessionDbSynced } from '../utils/adminLearningContentSessionSync';

interface UseAdminLearningContentRowsParams {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
  /** true면 매 load마다 서버 조회 (`forceRefresh`) */
  preferServerRows?: boolean;
}

function adminLearningRowsScopeId(
  workspaceId: string,
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  return `${workspaceId}|${gradeNumber}|${subCategory}|${templateName.trim()}`;
}

export function useAdminLearningContentRows({
  workspaceId,
  gradeNumber,
  subCategory,
  templateName,
  preferServerRows = false,
}: UseAdminLearningContentRowsParams) {
  const [rows, setRows] = useState<AdminLearningContentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestFetchTargetRef = useRef('');
  const fetchGenerationRef = useRef(0);
  const rowsOriginScopeIdRef = useRef('');

  const scopeTemplate = templateName.trim();
  if (scopeTemplate) {
    latestFetchTargetRef.current = adminLearningRowsScopeId(
      workspaceId,
      gradeNumber,
      subCategory,
      scopeTemplate
    );
  } else {
    latestFetchTargetRef.current = '';
  }

  const load = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      if (!scopeTemplate) {
        fetchGenerationRef.current += 1;
        rowsOriginScopeIdRef.current = '';
        setRows([]);
        setLoading(false);
        return [];
      }
      const fetchTarget = adminLearningRowsScopeId(
        workspaceId,
        gradeNumber,
        subCategory,
        scopeTemplate
      );
      const fetchId = ++fetchGenerationRef.current;
      const forceRefresh = options?.forceRefresh === true || preferServerRows;
      setLoading(true);
      setError(null);
      try {
        const loaded = await getAdminLearningContentRows(
          workspaceId,
          gradeNumber,
          subCategory,
          scopeTemplate,
          forceRefresh ? { forceRefresh: true } : undefined
        );
        if (fetchId !== fetchGenerationRef.current) return loaded;
        if (latestFetchTargetRef.current !== fetchTarget) return loaded;
        rowsOriginScopeIdRef.current = fetchTarget;
        setRows(loaded);
        return loaded;
      } catch (e) {
        if (fetchId !== fetchGenerationRef.current) return [];
        if (latestFetchTargetRef.current !== fetchTarget) return [];
        rowsOriginScopeIdRef.current = '';
        setError(e instanceof Error ? e.message : '학습내용 데이터를 불러오지 못했습니다.');
        setRows([]);
        return [];
      } finally {
        if (fetchId === fetchGenerationRef.current && latestFetchTargetRef.current === fetchTarget) {
          setLoading(false);
        }
      }
    },
    [gradeNumber, preferServerRows, workspaceId, subCategory, scopeTemplate]
  );

  useLayoutEffect(() => {
    if (!scopeTemplate) {
      fetchGenerationRef.current += 1;
      rowsOriginScopeIdRef.current = '';
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchTarget = adminLearningRowsScopeId(
      workspaceId,
      gradeNumber,
      subCategory,
      scopeTemplate
    );
    const fetchId = ++fetchGenerationRef.current;

    rowsOriginScopeIdRef.current = '';
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        if (
          !preferServerRows &&
          isAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, scopeTemplate)
        ) {
          const cached = await loadAdminLearningContentRowsCacheAsync(
            workspaceId,
            gradeNumber,
            subCategory,
            scopeTemplate
          );
          if (fetchId !== fetchGenerationRef.current) return;
          if (latestFetchTargetRef.current !== fetchTarget) return;
          if (cached.found) {
            rowsOriginScopeIdRef.current = fetchTarget;
            setRows(cached.rows);
            setError(null);
            setLoading(false);
            return;
          }
        }

        const loaded = await getAdminLearningContentRows(
          workspaceId,
          gradeNumber,
          subCategory,
          scopeTemplate,
          preferServerRows ? { forceRefresh: true } : undefined
        );
        if (fetchId !== fetchGenerationRef.current) return;
        if (latestFetchTargetRef.current !== fetchTarget) return;
        rowsOriginScopeIdRef.current = fetchTarget;
        setRows(loaded);
      } catch (e) {
        if (fetchId !== fetchGenerationRef.current) return;
        if (latestFetchTargetRef.current !== fetchTarget) return;
        rowsOriginScopeIdRef.current = '';
        setError(e instanceof Error ? e.message : '학습내용 데이터를 불러오지 못했습니다.');
        setRows([]);
      } finally {
        if (fetchId === fetchGenerationRef.current && latestFetchTargetRef.current === fetchTarget) {
          setLoading(false);
        }
      }
    })();

    return () => {
      fetchGenerationRef.current += 1;
    };
  }, [gradeNumber, preferServerRows, workspaceId, subCategory, scopeTemplate]);

  const saveRows = useCallback(
    async (nextRows: AdminLearningContentRowUpsert[]) => {
      if (!scopeTemplate) return [];
      setSaving(true);
      setError(null);
      try {
        const saved = await replaceAdminLearningContentRows(
          workspaceId,
          gradeNumber,
          subCategory,
          scopeTemplate,
          nextRows
        );
        const scopeId = adminLearningRowsScopeId(
          workspaceId,
          gradeNumber,
          subCategory,
          scopeTemplate
        );
        if (latestFetchTargetRef.current === scopeId) {
          rowsOriginScopeIdRef.current = scopeId;
          setRows(saved);
        }
        return saved;
      } catch (e) {
        setError(e instanceof Error ? e.message : '학습내용 데이터를 저장하지 못했습니다.');
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [gradeNumber, workspaceId, subCategory, scopeTemplate]
  );

  return useMemo(
    () => ({
      rows,
      loading,
      saving,
      error,
      reload: load,
      setRows,
      saveRows,
    }),
    [error, load, loading, rows, saveRows, saving]
  );
}
